import React, { Component } from "react";
import { Fade } from "react-reveal";
import { calendarData } from "../../portfolio.js";
import {
  getCurrentEthiopianDate,
  getEthiopianOrthodoxHolidays,
  formatEthiopianDate,
  gregorianToEthiopian,
  getFastingPeriodForDate,
} from "../../utils/ethiopianCalendar.js";
import "./CalendarSection.css";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../translations/translations";

class CalendarSection extends Component {
  constructor(props) {
    super(props);
    const today = new Date();
    this.state = {
      currentMonth: today.getMonth(),
      currentYear: today.getFullYear(),
      currentEthiopianDate: null,
      ethiopianHolidays: [],
      daysInMonth: this.getDaysInMonth(today.getMonth(), today.getFullYear()),
      firstDayOfMonth: new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      ).getDay(),
      selectedDate: null,
      showDateModal: false,
    };
  }

  getDaysInMonth(month, year) {
    return new Date(year, month + 1, 0).getDate();
  }

  /**
   * Check if a date is within the 1-year limit from today
   * Users CANNOT go back from the current month
   * Users can go up to 1 year forward from today, no further
   * @returns {boolean} true if date is allowed, false if it violates the limit
   */
  isDateWithinLimit(month, year, direction) {
    const today = new Date();
    const checkDate = new Date(year, month, 1);
    const currentMonthDate = new Date(today.getFullYear(), today.getMonth(), 1);

    if (direction === "prev") {
      // Cannot go back from current month at all
      return checkDate >= currentMonthDate;
    } else if (direction === "next") {
      // Can go forward up to 1 year from today
      const oneYearFromNow = new Date(
        today.getFullYear() + 1,
        today.getMonth(),
        1
      );
      return checkDate <= oneYearFromNow;
    }

    return true;
  }

  nextMonth = () => {
    let { currentMonth, currentYear } = this.state;
    currentMonth++;
    if (currentMonth > 11) {
      currentMonth = 0;
      currentYear++;
    }

    // Check if the new date is within the limit (cannot go more than 1 year forward)
    if (!this.isDateWithinLimit(currentMonth, currentYear, "next")) {
      // Don't navigate - we've exceeded the 1-year forward limit
      console.log(
        "Cannot navigate further forward. 1-year forward limit reached."
      );
      return;
    }

    this.setState({
      currentMonth,
      currentYear,
      daysInMonth: this.getDaysInMonth(currentMonth, currentYear),
      firstDayOfMonth: new Date(currentYear, currentMonth, 1).getDay(),
      selectedDate: null,
      showDateModal: false,
    });

    // Reload holidays for the new month
    this.loadHolidaysForMonth(currentMonth, currentYear);
  };

  previousMonth = () => {
    let { currentMonth, currentYear } = this.state;

    // Check what the previous month would be
    let newMonth = currentMonth - 1;
    let newYear = currentYear;
    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    }

    // Check if the new date is within the limit (cannot go back from current month)
    if (!this.isDateWithinLimit(newMonth, newYear, "prev")) {
      // Don't navigate - we've hit the limit (cannot go to past)
      console.log(
        "Cannot navigate to past. Users can only view current month or future months."
      );
      return;
    }

    currentMonth = newMonth;
    currentYear = newYear;

    this.setState({
      currentMonth,
      currentYear,
      daysInMonth: this.getDaysInMonth(currentMonth, currentYear),
      firstDayOfMonth: new Date(currentYear, currentMonth, 1).getDay(),
      selectedDate: null,
      showDateModal: false,
    });

    // Reload holidays for the new month
    this.loadHolidaysForMonth(currentMonth, currentYear);
  };

  handleDayClick = (day) => {
    const { currentMonth, currentYear } = this.state;
    const selectedDate = new Date(currentYear, currentMonth, day);
    this.setState({ selectedDate, showDateModal: true });
  };

  closeModal = () => {
    this.setState({ showDateModal: false });
  };

  /**
   * Feast days in the Ethiopian Orthodox Church
   * Maps Ethiopian month/day to feast name and description
   */
  getFeastDayInfo(ethMonth, ethDay) {
    const feastDays = {
      "1": {
        1: {
          name: "Ethiopian New Year (Enkutatash)",
          description: "Celebration of the Ethiopian New Year",
        },
        2: {
          name: "Ethiopian New Year Feast",
          description: "Continuation of New Year celebration",
        },
        3: {
          name: "Ethiopian New Year Feast",
          description: "Continuation of New Year celebration",
        },
        4: {
          name: "Ethiopian New Year Feast",
          description: "Continuation of New Year celebration",
        },
        5: {
          name: "Ethiopian New Year Feast",
          description: "Continuation of New Year celebration",
        },
      },
      "2": {
        21: {
          name: "Feast of St. Mary (Abreham)",
          description: "Celebration of Virgin Mary",
        },
      },
      "8": {
        15: {
          name: "Feast of Assumption of Mary (Filseta)",
          description: "Start of Assumption feast",
        },
        16: {
          name: "Feast of Assumption of Mary (Filseta)",
          description: "Assumption of Mary to heaven",
        },
        17: {
          name: "Feast of Assumption of Mary (Filseta)",
          description: "Continuation of Assumption feast",
        },
      },
      "9": {
        10: {
          name: "Feast of Meskerem/National Day",
          description: "Ethiopian New Year (Gregorian Sept 11)",
        },
        11: {
          name: "Feast of Meskerem/National Day",
          description: "Continuation of New Year celebration",
        },
      },
    };

    const monthFeastsObj = feastDays[ethMonth.toString()];
    return monthFeastsObj && monthFeastsObj[ethDay]
      ? monthFeastsObj[ethDay]
      : null;
  }

  /**
   * Check if meat is allowed on a given date
   * Meat is allowed on:
   * 1. Sundays (except during fasting periods)
   * 2. Major feast days
   * 3. Non-fasting days outside of fast periods
   * @returns {Object} Meat info with name and description, or null
   */
  getMeatAllowedInfo(gregorianDate, ethDate, isDayFasting) {
    // No meat during any fasting period
    if (isDayFasting) return null;

    // Check if it's a Sunday
    const dayOfWeek = gregorianDate.getDay();
    const isMonday = dayOfWeek === 1;
    const isSunday = dayOfWeek === 0;

    // Meat allowed info for Sundays
    if (isSunday) {
      return {
        name: "Meat is Allowed",
        description: "Sunday - Meat consumption is permitted",
        type: "meat-allowed",
      };
    }

    // Meat allowed on major feast days
    const majorFeastDays = [
      { month: 1, days: [1, 2, 3, 4, 5], name: "Ethiopian New Year Feast" },
      { month: 2, days: [21], name: "Feast of St. Mary" },
      { month: 8, days: [15, 16, 17], name: "Assumption Feast" },
      { month: 9, days: [10, 11], name: "Meskerem Festival" },
    ];

    const isMajorFeast = majorFeastDays.some(
      (feast) =>
        feast.month === ethDate.month && feast.days.includes(ethDate.day)
    );

    if (isMajorFeast) {
      return {
        name: "Meat is Allowed",
        description: "Major feast day - Meat consumption is permitted",
        type: "meat-allowed",
      };
    }

    // Meat allowed on regular non-fasting weekdays (except Wednesdays and Fridays for weekly fast)
    // Wednesday and Friday are always fasting days (weekly fasts), so they should already return null above
    const isRegularDay = !isDayFasting && dayOfWeek !== 3 && dayOfWeek !== 5; // Not Wed/Fri

    if (isRegularDay && dayOfWeek !== 0) {
      // Not Sunday (already handled)
      return {
        name: "Meat is Allowed",
        description:
          "Regular day outside fasting period - Meat consumption is permitted",
        type: "meat-allowed",
      };
    }

    return null;
  }

  getDayStatus(gregorianDate) {
    const ethDate = gregorianToEthiopian(gregorianDate);
    if (!ethDate) return "normal";

    const { ethiopianHolidays } = this.state;

    // Check if it's a holiday
    const holiday = ethiopianHolidays.find(
      (h) => h.ethMonth === ethDate.month && h.ethDay === ethDate.day
    );

    if (holiday) return "holiday";

    // Check if it's a fasting day using the DATE'S Ethiopian year, not today's year
    const fastingPeriod = getFastingPeriodForDate(
      ethDate.month,
      ethDate.day,
      ethDate.year, // Use the year of the date being checked, not currentEthiopianDate
      "amharic" // Always use Amharic for fasting period names
    );

    if (fastingPeriod) return "fast";

    return "normal";
  }

  /**
   * Get Ethiopian year(s) that overlap with the current Gregorian month
   * A Gregorian month can span two Ethiopian years (around Sept 11 boundary)
   */
  getEthiopianYearsForMonth(month, year) {
    // Get the first and last days of the Gregorian month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const firstEthDay = gregorianToEthiopian(firstDay);
    const lastEthDay = gregorianToEthiopian(lastDay);

    // Collect unique Ethiopian years
    const ethYears = new Set();
    if (firstEthDay) ethYears.add(firstEthDay.year);
    if (lastEthDay) ethYears.add(lastEthDay.year);

    return Array.from(ethYears);
  }

  /**
   * Load holidays for the current Gregorian month
   * This may include holidays from 1-2 Ethiopian years
   */
  loadHolidaysForMonth(month, year) {
    const ethYears = this.getEthiopianYearsForMonth(month, year);
    let allHolidays = [];

    // Get holidays for each Ethiopian year that overlaps this month
    ethYears.forEach((ethYear) => {
      const holidays = getEthiopianOrthodoxHolidays(ethYear, "amharic");
      allHolidays = allHolidays.concat(holidays);
    });

    this.setState({ ethiopianHolidays: allHolidays });
  }

  componentDidMount() {
    const today = new Date();
    const ethDate = getCurrentEthiopianDate();
    this.setState({ currentEthiopianDate: ethDate });

    // Load holidays for the current month
    this.loadHolidaysForMonth(today.getMonth(), today.getFullYear());
  }

  getCalendarDays() {
    const {
      currentMonth,
      currentYear,
      daysInMonth,
      firstDayOfMonth,
    } = this.state;
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }

  render() {
    const theme = this.props.theme;
    const { language } = this.props;
    const {
      currentEthiopianDate,
      ethiopianHolidays,
      currentMonth,
      currentYear,
      daysInMonth,
      selectedDate,
      showDateModal,
    } = this.state;

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const calendarDays = this.getCalendarDays();
    const today = new Date();

    // Check if Previous button should be disabled (cannot go back from current month)
    let prevMonth = currentMonth - 1;
    let prevYear = currentYear;
    if (prevMonth < 0) {
      prevMonth = 11;
      prevYear--;
    }
    const canGoPrevious = this.isDateWithinLimit(prevMonth, prevYear, "prev");

    // Check if Next button should be disabled (cannot go more than 1 year forward)
    let nextMonth = currentMonth + 1;
    let nextYear = currentYear;
    if (nextMonth > 11) {
      nextMonth = 0;
      nextYear++;
    }
    const canGoNext = this.isDateWithinLimit(nextMonth, nextYear, "next");

    // Get info for selected date
    let selectedEthDate = null;
    let selectedDayStatus = null;
    let selectedHoliday = null;
    let selectedFastDay = null;
    let selectedFeastDay = null;
    let selectedMeatAllowed = null;
    if (selectedDate) {
      selectedEthDate = gregorianToEthiopian(selectedDate);
      selectedDayStatus = this.getDayStatus(selectedDate);

      // Get holiday info
      selectedHoliday = ethiopianHolidays.find(
        (h) =>
          h.ethMonth === selectedEthDate.month &&
          h.ethDay === selectedEthDate.day
      );

      // Get fast day info
      if (selectedDayStatus === "fast") {
        selectedFastDay = getFastingPeriodForDate(
          selectedEthDate.month,
          selectedEthDate.day,
          selectedEthDate.year,
          "amharic" // Always use Amharic for fasting period names
        );
      }

      // Get meat-allowed info (only on regular days, not holidays or fasting days)
      if (selectedDayStatus === "normal") {
        selectedMeatAllowed = this.getMeatAllowedInfo(
          selectedDate,
          selectedEthDate,
          false
        );
      }
    }

    const getStatusLabel = (status) => {
      switch (status) {
        case "holiday":
          return getTranslation(language, "holiday");
        case "fast":
          return getTranslation(language, "fast_day");
        default:
          return getTranslation(language, "normal");
      }
    };

    const getStatusColor = (status) => {
      switch (status) {
        case "holiday":
          return "#FF8C42";
        case "fast":
          return "#06A77D";
        default:
          return "#E0E0E0";
      }
    };

    return (
      <div className="calendar-main">
        {/* Calendar Header Section */}
        <div className="basic-calendar">
          <Fade bottom duration={2000} distance="40px">
            <div className="calendar-heading-div">
              <div className="calendar-heading-text-div">
                <h1
                  className="calendar-heading-text"
                  style={{ color: theme.text }}
                >
                  {getTranslation(language, "church_calendar")}
                </h1>
                <p
                  className="calendar-header-detail-text subTitle"
                  style={{ color: theme.secondaryText }}
                >
                  {getTranslation(language, "calendar_desc")}
                </p>
              </div>
            </div>
          </Fade>
        </div>

        {/* Current Month Calendar with Ethiopian Dates */}
        <Fade bottom duration={1500} distance="40px">
          <div className="month-calendar-section">
            {/* Month Navigation */}
            <div className="month-navigation">
              <button
                className="month-nav-btn prev-btn"
                onClick={this.previousMonth}
                disabled={!canGoPrevious}
                style={{
                  color: canGoPrevious ? theme.highlight : "#999",
                  borderColor: canGoPrevious ? theme.highlight : "#999",
                  opacity: canGoPrevious ? 1 : 0.5,
                  cursor: canGoPrevious ? "pointer" : "not-allowed",
                }}
                title={
                  canGoPrevious
                    ? "Previous month"
                    : getTranslation(language, "prev_month_disabled")
                }
              >
                {getTranslation(language, "prev_month")}
              </button>
              <h2 style={{ color: theme.text }} className="month-title">
                {monthNames[currentMonth]} {currentYear}
              </h2>
              <button
                className="month-nav-btn next-btn"
                onClick={this.nextMonth}
                disabled={!canGoNext}
                style={{
                  color: canGoNext ? theme.highlight : "#999",
                  borderColor: canGoNext ? theme.highlight : "#999",
                  opacity: canGoNext ? 1 : 0.5,
                  cursor: canGoNext ? "pointer" : "not-allowed",
                }}
                title={
                  canGoNext
                    ? "Next month"
                    : getTranslation(language, "next_month_disabled")
                }
              >
                {getTranslation(language, "next_month")}
              </button>
            </div>

            {/* Navigation Limit Notice */}
            {!canGoPrevious && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "10px",
                  fontSize: "0.85rem",
                  color: theme.secondaryText,
                  fontStyle: "italic",
                }}
              >
                ℹ️ {getTranslation(language, "current_month_message")}
              </div>
            )}

            {!canGoNext && (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "10px",
                  fontSize: "0.85rem",
                  color: theme.secondaryText,
                  fontStyle: "italic",
                }}
              >
                ℹ️ {getTranslation(language, "forward_limit_message")}
              </div>
            )}

            {/* Calendar Legend */}
            <div className="calendar-legend">
              <div className="legend-item">
                <span className="legend-color holiday"></span>
                <span style={{ color: theme.text }}>
                  {getTranslation(language, "holiday")}
                </span>
              </div>
              <div className="legend-item">
                <span className="legend-color fast"></span>
                <span style={{ color: theme.text }}>
                  {getTranslation(language, "fast_day")}
                </span>
              </div>
              <div className="legend-item">
                <span className="legend-color normal"></span>
                <span style={{ color: theme.text }}>
                  {getTranslation(language, "normal")}
                </span>
              </div>
            </div>

            {/* Day Headers */}
            <div className="calendar-grid">
              {dayNames.map((day) => (
                <div
                  key={`day-${day}`}
                  className="calendar-day-header"
                  style={{ color: theme.text }}
                >
                  {day}
                </div>
              ))}

              {/* Calendar Days */}
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return (
                    <div
                      key={`empty-${index}`}
                      className="calendar-cell empty"
                    ></div>
                  );
                }

                const currentDate = new Date(currentYear, currentMonth, day);
                const isToday =
                  day === today.getDate() &&
                  currentMonth === today.getMonth() &&
                  currentYear === today.getFullYear();
                const ethDate = gregorianToEthiopian(currentDate);
                const dayStatus = this.getDayStatus(currentDate);

                return (
                  <div
                    key={`day-${day}`}
                    className={`calendar-cell ${dayStatus} ${
                      isToday ? "today" : ""
                    }`}
                    onClick={() => this.handleDayClick(day)}
                    style={{
                      borderColor: isToday ? theme.highlight : undefined,
                      backgroundColor: isToday
                        ? `${theme.highlight}20`
                        : undefined,
                      cursor: "pointer",
                    }}
                  >
                    <div
                      className="gregorian-day"
                      style={{ color: theme.text }}
                    >
                      {day}
                    </div>
                    {ethDate && (
                      <div
                        className="ethiopian-day"
                        style={{ color: theme.secondaryText }}
                      >
                        {ethDate.day}/{ethDate.month}
                      </div>
                    )}
                    <div className="day-status-badge"></div>
                  </div>
                );
              })}
            </div>
          </div>
        </Fade>

        {/* Date Details Modal */}
        {showDateModal && selectedDate && selectedEthDate && (
          <div className="modal-overlay" onClick={this.closeModal}>
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              style={{ borderColor: theme.highlight }}
            >
              <div
                className="modal-header"
                style={{ backgroundColor: getStatusColor(selectedDayStatus) }}
              >
                <h2 style={{ color: "#fff", margin: 0 }}>
                  {monthNames[currentMonth]} {selectedDate.getDate()},{" "}
                  {currentYear}
                </h2>
                <button className="modal-close" onClick={this.closeModal}>
                  ✕
                </button>
              </div>

              <div className="modal-body" style={{ color: theme.text }}>
                {/* Gregorian Date */}
                <div className="date-info-section">
                  <h3 style={{ color: theme.text }}>Gregorian Date</h3>
                  <p className="date-value">
                    {selectedDate.toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>

                {/* Ethiopian Date */}
                <div className="date-info-section">
                  <h3 style={{ color: theme.text }}>Ethiopian Calendar</h3>
                  <p className="date-value">
                    {selectedEthDate.day} {selectedEthDate.monthName}{" "}
                    {selectedEthDate.year} ዓ.ም
                  </p>
                  <p
                    className="date-subtitle"
                    style={{ color: theme.secondaryText }}
                  >
                    ({selectedEthDate.day}/{selectedEthDate.month}/
                    {selectedEthDate.year})
                  </p>
                </div>

                {/* Day Status */}
                <div className="date-info-section">
                  <h3 style={{ color: theme.text }}>Status</h3>
                  <div
                    className="status-badge"
                    style={{
                      backgroundColor: getStatusColor(selectedDayStatus),
                    }}
                  >
                    {getStatusLabel(selectedDayStatus)}
                  </div>
                </div>

                {/* Holiday Info */}
                {selectedHoliday && (
                  <div className="date-info-section">
                    <h3 style={{ color: theme.text }}>Holiday</h3>
                    <p className="date-value">{selectedHoliday.name}</p>
                    <p
                      className="date-subtitle"
                      style={{ color: theme.secondaryText }}
                    >
                      {selectedHoliday.description}
                    </p>
                  </div>
                )}

                {/* Fast Day Info */}
                {selectedFastDay && (
                  <div className="date-info-section">
                    <h3 style={{ color: theme.text }}>Fasting Day</h3>
                    <p className="date-value">{selectedFastDay.name}</p>
                    <p
                      className="date-subtitle"
                      style={{ color: theme.secondaryText }}
                    >
                      {selectedFastDay.description}
                    </p>
                  </div>
                )}

                {/* Meat Allowed Info */}
                {selectedMeatAllowed && (
                  <div className="date-info-section">
                    <h3 style={{ color: theme.text }}>
                      🍖 Dietary Information
                    </h3>
                    <p className="date-value" style={{ color: "#27AE60" }}>
                      {selectedMeatAllowed.name}
                    </p>
                    <p
                      className="date-subtitle"
                      style={{ color: theme.secondaryText }}
                    >
                      {selectedMeatAllowed.description}
                    </p>
                  </div>
                )}

                {/* Additional Info */}
                <div className="date-info-section">
                  <p
                    style={{
                      color: theme.secondaryText,
                      fontSize: "0.9rem",
                      fontStyle: "italic",
                    }}
                  >
                    Click outside or press ✕ to close
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Current Ethiopian Date Display */}
        {currentEthiopianDate && (
          <Fade bottom duration={1500} distance="40px">
            <div
              className="ethiopian-date-display"
              style={{
                color: theme.text,
                borderColor: theme.highlight,
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              }}
            >
              <h3 style={{ color: theme.highlight }}>
                Today in Ethiopian Calendar
              </h3>
              <p className="ethiopian-date-large" style={{ color: theme.text }}>
                {formatEthiopianDate(currentEthiopianDate)}
              </p>
              <p
                className="ethiopian-date-gregorian"
                style={{ color: theme.secondaryText }}
              >
                (Gregorian:{" "}
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
                )
              </p>
            </div>
          </Fade>
        )}
      </div>
    );
  }
}

// Wrapper component to inject language from context
const CalendarSectionWithLanguage = (props) => {
  const { language } = useLanguage();
  return <CalendarSection {...props} language={language} />;
};

export default CalendarSectionWithLanguage;
